import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import type { FiatPrice } from "@b2bportal/price-freeze-api";

import { trackEvent } from "@hopper-b2b/api";
import {
  type AirPriceFreezeMachineContext,
  AirPriceFreezePurchaseState,
  getChildState,
  type IContactInfo,
  PriceFreezeOfferEvent,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useExperiment } from "@hopper-b2b/experiments";
import {
  EntryButtonEnum,
  PreviousFlightEnum,
  PriceFreezeEntryEnum,
  type PriceFreezeHistoryState,
  PriceFreezeOfferPage,
  PriceFreezeTrackingEvents,
} from "@hopper-b2b/fintech";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import { B2BLoadingPopup, ErrorModal } from "@hopper-b2b/ui";
import {
  isEmailValid,
  removeTimezone,
  updateTrackingPropertiesRequest,
  useUberBridge,
} from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { FEATURE_FLAGS } from "../../../../App";
import AirplaneIcon from "../../../../assets/client/airplane.svg";
import Chevron from "../../../../assets/client/chevron.svg";
import AirplaneWhiteIcon from "../../../../assets/client/darkMode/airplane-white.svg";
import InfoGrey from "../../../../assets/client/darkMode/info-grey.svg";
import LocationMarkerWhite from "../../../../assets/client/darkMode/location-marker-white.svg";
import Error from "../../../../assets/client/error.svg";
import Info from "../../../../assets/client/info.svg";
import LocationMarker from "../../../../assets/client/location-marker.svg";

import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { Event, type TEvent } from "../../events";
import { Selectors } from "../../selectors";
import type { PassengerMap } from "../../types";

const DATE_FORMAT = "MMM D";

export const PriceFreezeOffer = () => {
  const [state, send] = useCheckoutState<
    TEvent,
    AirPriceFreezeMachineContext
  >();
  const { t, formatFiatCurrency } = useI18nContext();
  const history = useHistory();
  const { setHeader } = useUberBridge();
  const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);

  useEffect(() => {
    setHeader({
      title: t("priceFreezePurchase.documentTitle"),
    });
  }, [setHeader, t]);

  //ERROR
  const childState = getChildState(state.value);
  const searchAgain = () => {
    history.push(PATH_FLIGHTS_SEARCH);
  };

  //Get trip details
  const tripDetails = useSelector(Selectors.getTripDetails);
  const offerData = useSelector(Selectors.getPriceFreezeOfferData);

  const isPageReady = !!tripDetails && !!offerData;

  // BANNER
  const originName = useSelector(Selectors.getOriginName);
  const destinationName = useSelector(Selectors.getDestinationName);
  const departureDateTime = useSelector(Selectors.getDepartureDate);
  const returnDateTime = useSelector(Selectors.getReturnDate);
  const departureDate = dayjs(removeTimezone(departureDateTime)).format(
    DATE_FORMAT
  );
  const returnDate = dayjs(removeTimezone(returnDateTime)).format(DATE_FORMAT);

  const isRoundTrip = useSelector(Selectors.getIsRoundTrip);
  const tripType = isRoundTrip ? t("flightRoundTrip") : t("flightOneWay");

  const bannerTitle = t("originToDestination", {
    origin: originName,
    destination: destinationName,
  });
  const bannerSubtitle = `${departureDate} ${
    isRoundTrip ? `- ${returnDate}` : ""
  } • ${tripType}`;

  //DURATION
  const handleDurationChange = (newValue: number) => {
    send({
      type: PriceFreezeOfferEvent.SET_PRICE_FREEZE_DURATION_INDEX,
      duration: newValue,
    });

    const changedPriceFreezeDurationProperties =
      Selectors.changedPriceFreezeDurationPropertiesSelector(state);
    trackEvent({
      eventName: PriceFreezeTrackingEvents.CHANGED_DURATION,
      ...changedPriceFreezeDurationProperties,
    });
  };
  const selectedOfferDataIndex = useSelector(
    Selectors.getSelectedPriceFreezeDurationIndex
  );

  // OFFER CARD
  const flightPricePerPax = useSelector(Selectors.getFlightPricePerPax)?.fiat;
  const priceFreezeOfferPricePerPax = useSelector(
    Selectors.getPriceFreezeOfferPricePerPax
  )?.fiat;
  const priceFreezeOfferCap = useSelector(
    Selectors.getPriceFreezeOfferCap
  )?.fiat;

  const getPriceText = (
    fiat: FiatPrice | undefined,
    options?: Intl.NumberFormatOptions
  ) => (fiat ? formatFiatCurrency(fiat, options) : "");

  const flightPricePerPaxText = getPriceText(flightPricePerPax);

  //Special case remove decimals from PF price.
  const priceFreezeOfferPricePerPaxText = getPriceText(
    priceFreezeOfferPricePerPax,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }
  );

  const priceFreezeOfferCapText = getPriceText(priceFreezeOfferCap);

  const selectedOfferData = useSelector(Selectors.getSelectedOfferData)!;

  //CONTACT INFORMATION
  const updateContactInfo = useCallback(
    (contactInfo: IContactInfo) => {
      send({
        type: PriceFreezeOfferEvent.SET_CONTACT_INFO,
        contactInfo: contactInfo,
      });
    },
    [send]
  );

  const contactInfo = useSelector(Selectors.getContactInformation)!;

  //DETAILS
  const passengerMap = useSelector(Selectors.getPassengerMap);
  const onPassengerCountChange = (passengerMap: PassengerMap) => {
    send({
      type: PriceFreezeOfferEvent.SET_PASSENGER_MAP,
      passengerMap: passengerMap,
    });
    trackEvent({
      eventName: PriceFreezeTrackingEvents.EDIT_TRAVELERS,
      properties: Selectors.editTravelerEventPropSelector(state),
    });
  };

  const fareDetails = useSelector(Selectors.getFareDetails)!;

  const airports = useSelector(Selectors.getAirports);

  const freezeOfferTotalPrice = useSelector(
    Selectors.getPriceFreezeOfferTotalPrice
  )?.fiat;

  const freezeOfferTotalPriceText = useMemo(
    () =>
      freezeOfferTotalPrice ? formatFiatCurrency(freezeOfferTotalPrice) : "..",
    [freezeOfferTotalPrice]
  );

  // CTA
  const priceFreezeOffer = useSelector(Selectors.getPriceFreezeOffer);
  const canContinueToPayment =
    contactInfo?.email && isEmailValid(contactInfo.email) && !!priceFreezeOffer;

  const scheduleQuote = () => {
    // Set contact info into cart quote before going to next step.
    send({
      type: PriceFreezeOfferEvent.SET_CONTACT_INFO,
      contactInfo: contactInfo,
    });

    send({
      type: Event.NEXT,
    });
  };

  //

  useEffect(() => {
    if (isPageReady) {
      const {
        priceFreezeEntry,
        entryButton,
        previousFlight,
      }: PriceFreezeHistoryState = history.location.state || {};
      const priceFreezeReviewDetailsProperties =
        Selectors.priceFreezeReviewDetailsPropertiesSelector(state);

      const trackingProperties = updateTrackingPropertiesRequest(
        priceFreezeReviewDetailsProperties,
        [],
        {
          price_freeze_entry:
            priceFreezeEntry ?? PriceFreezeEntryEnum.TRIP_SUMMARY,
          entry_button: entryButton ?? EntryButtonEnum.INFO_MODAL,
          previous_flight: previousFlight ?? PreviousFlightEnum.CHOSEN_FLIGHT,
        }
      );

      trackEvent({
        eventName: PriceFreezeTrackingEvents.PRICE_FREEZE_REVIEW_DETAILS,
        ...trackingProperties,
      });
    }
  }, [isPageReady]);

  const assets = useMemo(() => {
    const isDarkMode = enableDarkMode && getDarkModePreferred();
    return isDarkMode
      ? {
          info: InfoGrey,
          chevron: Chevron,
          airplane: AirplaneWhiteIcon,
          locationMarker: LocationMarkerWhite,
        }
      : {
          chevron: Chevron,
          info: Info,
          airplane: AirplaneIcon,
          locationMarker: LocationMarker,
        };
  }, [enableDarkMode]);

  return (
    <>
      {isPageReady ? (
        <PriceFreezeOfferPage
          tripDetails={tripDetails}
          bannerTitle={bannerTitle}
          bannerSubtitle={bannerSubtitle}
          flightPricePerPaxText={flightPricePerPaxText}
          priceFreezeOfferPricePerPaxText={priceFreezeOfferPricePerPaxText}
          offerData={offerData}
          selectedOfferData={selectedOfferData}
          handleContactInfoChange={updateContactInfo}
          contactInfo={contactInfo}
          canContinueToPayment={!!canContinueToPayment}
          onNextClick={scheduleQuote}
          handleDurationChange={handleDurationChange}
          passengerMap={passengerMap}
          fareDetails={fareDetails}
          onPassengerCountChange={onPassengerCountChange}
          selectedOfferDataIndex={selectedOfferDataIndex}
          priceFreezeTotalAmount={freezeOfferTotalPriceText}
          offerCapAmount={priceFreezeOfferCapText}
          airports={airports}
          assets={assets}
        />
      ) : (
        <B2BLoadingPopup
          open={true}
          fullScreen
          message={t("loading")}
          popupSize={"mobile"}
        />
      )}
      <ErrorModal
        open={childState === AirPriceFreezePurchaseState.error}
        title={t("checkoutError.genericErrorTitle")}
        subtitle={t("checkoutError.genericErrorSubtitle")}
        primaryButtonText={t("checkoutError.searchAgain")}
        primaryOnClick={searchAgain}
        image={Error}
      />
    </>
  );
};
